import { useMutation } from '@apollo/client';

import {
  ADD_ACTIVITY_LOG,
  ASSIGN_MEDICAL_REFERRAL,
  ASSIGN_PRESCRIBER,
  CREATE_ISSUE,
  CREATE_MEDICAL_REFERRAL,
  CREATE_NOTE,
  CREATE_PRESCRIPTION,
  DELETE_NOTE,
  DELETE_PRESCRIPTION,
  PENDING_MEDICAL_REFERRAL,
  UPDATE_CONSULTING_STATUS,
  UPDATE_CREATE_NOTE,
  UPDATE_PRESCRIPTION,
  UPDATE_PRESCRIPTION_STATUS,
  UPDATE_STUDY_STATUS,
  UPDATE_USER_PHN,
} from '../../graphql/mutations';
import { CURRENT_USER, PRESCRIPTIONS, STUDIES } from '../../graphql/queries';
import {
  NotePayload,
  PreferredPharmacyInput,
  PrescriptionInput,
  PrescriptionPayload,
  ReferrerInput,
  StudyFilterInput,
  UpdatePrescriptionPayload,
} from '../../types';

export const useAddActivityLogMutation = () => {
  const [addActivityLog, { loading, error }] = useMutation(ADD_ACTIVITY_LOG);

  const mutation: any = [
    ({ studyId, log, category }: { studyId: string; log: string; category?: string }) =>
      addActivityLog({
        variables: {
          studyId: studyId,
          log: log,
          category: category
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};
export const useCreateMedicalReferralMutation = () => {
  const [createMedicalReferral, { loading, error }] = useMutation(CREATE_MEDICAL_REFERRAL);

  const mutation: any = [
    ({ studyId, referral }: { studyId: string; referral: boolean }) =>
      createMedicalReferral({
        variables: {
          studyId: studyId,
          referral: referral,
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const usePendingMedicalReferralMutation = () => {
  const [pendingMedicalReferral, { loading, error }] = useMutation(PENDING_MEDICAL_REFERRAL);

  const mutation: any = [
    ({ studyId }: { studyId: string }) =>
    pendingMedicalReferral({
        variables: {
          studyId: studyId,
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useAssignMedicalReferralMutation = () => {
  const [assignMedicalReferrer, { loading, error }] = useMutation(ASSIGN_MEDICAL_REFERRAL);

  const mutation: any = [
    ({
      studyId,
      referrer,
      referralType,
      studyFilter,
      unassign,
    }: {
      studyId: string;
      referrer: ReferrerInput;
      referralType: string;
      studyFilter: StudyFilterInput;
      unassign: boolean;
    }) =>
      assignMedicalReferrer({
        variables: {
          studyId: studyId,
          referrer: referrer,
          referralType: referralType,
          unassign: unassign,
        },
        refetchQueries: [
          {
            query: STUDIES,
            variables: {
              studyFilter,
            },
            context: {
              clientName: 'apps',
            },
          },
        ],
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useAssignPrescriberMutation = () => {
  const [assignMedicalReferrer, { loading, error }] = useMutation(ASSIGN_PRESCRIBER);

  const mutation: any = [
    ({
      studyId,
      prescriber,
      studyFilter,
      unassign,
    }: {
      studyId: string;
      prescriber: ReferrerInput;
      studyFilter: StudyFilterInput;
      unassign: boolean;
    }) =>
      assignMedicalReferrer({
        variables: {
          studyId: studyId,
          prescriber: prescriber,
          unassign: unassign,
        },
        refetchQueries: [
          {
            query: STUDIES,
            variables: {
              studyFilter,
            },
            context: {
              clientName: 'apps',
            },
          },
        ],
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

type StatusType = {
  statusKey: string;
  statusValue?: string;
  statusValueBoolean?: boolean;
};

export const useUpdateStudyStatus = () => {
  const [updateStudyStatus, { loading, error }] = useMutation(UPDATE_STUDY_STATUS);

  const mutation: any = [
    ({ studyId, statusList }: { studyId: string; statusList: Array<StatusType> }) =>
      updateStudyStatus({
        variables: {
          studyId: studyId,
          statusList: statusList,
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useCreatePrescription = () => {
  const [createPrescriptionMutation, { loading, error }] = useMutation(CREATE_PRESCRIPTION);

  const mutation: any = [
    ({ prescription, pharmacy }: PrescriptionPayload) =>
      createPrescriptionMutation({
        variables: {
          prescription,
          pharmacy,
        },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];

  const createPrescription = async (
    prescription?: PrescriptionInput,
    pharmacy?: PreferredPharmacyInput,
  ) =>
    createPrescriptionMutation({
      variables: {
        prescription: prescription,
        pharmacy: pharmacy,
      },
      context: {
        clientName: 'hi',
      },
      refetchQueries: [PRESCRIPTIONS, CURRENT_USER],
    });

  return { mutation, createPrescription, createPrescriptionMutation };
};

export const useUpdatePrescription = () => {
  const [updatePrescriptionMutation, { loading, error }] = useMutation(UPDATE_PRESCRIPTION);

  const mutation: any = [
    ({ prescriptionId, prescription, preferredPharmacy }: UpdatePrescriptionPayload) =>
      updatePrescriptionMutation({
        variables: {
          prescriptionId,
          prescription,
          preferredPharmacy,
        },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];

  const updatePrescription = async (
    prescriptionId: string,
    prescription?: string | null,
    pharmacy?: PreferredPharmacyInput | null,
    signature?: string | null,
    pickupDate?: string | null,
    pharmacyRepresentativeName?: string | null,
    sentDate?: string | null,
  ) =>
    updatePrescriptionMutation({
      variables: {
        prescriptionId: prescriptionId,
        prescription: prescription,
        preferredPharmacy: pharmacy || {},
        signature: signature,
        pickupDate: pickupDate,
        pharmacyRepresentativeName: pharmacyRepresentativeName,
        sentDate: sentDate,
      },
      context: {
        clientName: 'hi',
      },
      refetchQueries: [PRESCRIPTIONS],
    });

  return { mutation, updatePrescriptionMutation, updatePrescription };
};

export const useDeletePrescription = () => {
  const [deletePrescription, { loading, error }] = useMutation(DELETE_PRESCRIPTION);

  const mutation: any = [
    ({ prescriptionId }: { prescriptionId: string }) =>
      deletePrescription({
        variables: {
          prescriptionId,
        },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];

  return { mutation, deletePrescription };
};

export const useCreateNoteMutation = () => {
  const [createNote, { loading, error }] = useMutation(CREATE_NOTE);

  const mutation: any = [
    ({ note }: NotePayload) =>
      createNote({
        variables: { note },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];
  return { mutation, createNote };
};

export const useUpdateNoteMutation = () => {
  const [updateNote, { loading, error }] = useMutation(UPDATE_CREATE_NOTE);

  const mutation: any = [
    ({ note }: NotePayload) =>
      updateNote({
        variables: { note },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];
  return { mutation, updateNote };
};

export const useDeleteNoteMutation = () => {
  const [deleteNote, { loading, error }] = useMutation(DELETE_NOTE);

  const mutation: any = [
    ({ noteId }: { noteId: string }) =>
      deleteNote({
        variables: { noteId },
        context: {
          clientName: 'hi',
        },
      }),
    loading,
    error,
  ];
  return { mutation, deleteNote };
};

export const useUpdateUserPHNMutation = () => {
  const [updateUserSignature, { loading, error }] = useMutation(UPDATE_USER_PHN);

  const mutation: any = [
    ({ userId, phn }: { userId: string; phn: string }) =>
      updateUserSignature({
        variables: {
          userId,
          user: {
            phn,
          },
        },
        context: {
          clientName: 'pii',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useCreateIssueMutation = () => {
  const [createIssue, { loading, error }] = useMutation(CREATE_ISSUE);

  const mutation: any = [
    ({ issueInput }: { issueInput: { summary: string; description: string } }) =>
      createIssue({
        variables: { issueInput },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useUpdatePrescriptionStatusMutation = () => {
  const [updatePrescriptionStatus, { loading, error }] = useMutation(UPDATE_PRESCRIPTION_STATUS);

  const mutation: any = [
    ({ studyId, prescriptionStatus }: { studyId: string; prescriptionStatus: string }) =>
      updatePrescriptionStatus({
        variables: {
          studyId,
          prescriptionStatus,
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};

export const useUpdateConsultingStatusMutation = () => {
  const [updateConsultingStatus, { loading, error }] = useMutation(UPDATE_CONSULTING_STATUS);

  const mutation: any = [
    ({
      studyId,
      scheduledStatus,
      eventId,
      inviteeId,
      consultTime,
    }: {
      studyId: string;
      scheduledStatus?: string;
      eventId?: string;
      inviteeId?: string;
      consultTime?: number;
    }) =>
      updateConsultingStatus({
        variables: {
          studyId,
          scheduledStatus,
          eventId,
          inviteeId,
          consultTime,
        },
        context: {
          clientName: 'apps',
        },
      }),
    loading,
    error,
  ];

  return mutation;
};
